import api from '../config/api';

class FollowUpNotificationService {
  /**
   * Get upcoming follow-ups (within 15 minutes) from database
   * @returns {Promise<Object>} { success, data, count }
   */
  static async getUpcomingNotifications() {
    try {
      const response = await api.get('/follow-up-notifications/upcoming');
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error fetching upcoming notifications:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.response?.data?.message || 'Failed to fetch notifications'
      };
    }
  }

  /**
   * Get all pending follow-ups from database
   * @returns {Promise<Object>} { success, data, count }
   */
  static async getAllPendingFollowUps() {
    try {
      const response = await api.get('/follow-up-notifications/all');
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error fetching all follow-ups:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.response?.data?.message || 'Failed to fetch follow-ups'
      };
    }
  }

  /**
   * Get overdue follow-ups from database
   * @returns {Promise<Object>} { success, data, count }
   */
  static async getOverdueFollowUps() {
    try {
      const response = await api.get('/follow-up-notifications/overdue');
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error fetching overdue follow-ups:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.response?.data?.message || 'Failed to fetch overdue follow-ups'
      };
    }
  }

  /**
   * Dismiss a follow-up notification
   * @param {string} leadId - Lead ID
   * @param {string} followUpId - Follow-up ID
   * @returns {Promise<Object>} { success, message }
   */
  static async dismissNotification(leadId, followUpId) {
    try {
      const response = await api.put(`/follow-up-notifications/${leadId}/${followUpId}/dismiss`);
      return {
        success: true,
        message: response.data.message || 'Notification dismissed'
      };
    } catch (error) {
      console.error('Error dismissing notification:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to dismiss notification'
      };
    }
  }

  /**
   * Sync localStorage with database (for backward compatibility)
   * This will fetch from database and update localStorage
   */
  static async syncWithLocalStorage() {
    try {
      const result = await this.getAllPendingFollowUps();
      
      if (result.success && result.data.length > 0) {
        // Update localStorage with database data
        localStorage.setItem('crm-followups', JSON.stringify(result.data));
        console.log(`✅ Synced ${result.count} follow-ups from database to localStorage`);
        
        // Dispatch event to notify components
        window.dispatchEvent(new Event('followup-updated'));
        
        return { success: true, count: result.count };
      } else {
        console.log('ℹ️ No follow-ups to sync');
        return { success: true, count: 0 };
      }
    } catch (error) {
      console.error('Error syncing with localStorage:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Format notification for display
   * @param {Object} notification - Notification object from database
   * @returns {Object} Formatted notification
   */
  static formatNotification(notification) {
    return {
      ...notification,
      formattedTime: new Date(notification.scheduledDate).toLocaleString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: 'short'
      }),
      priorityColor: this.getPriorityColor(notification.priority),
      typeIcon: this.getTypeIcon(notification.type)
    };
  }

  /**
   * Get priority color class
   * @param {string} priority - Priority level
   * @returns {string} CSS class
   */
  static getPriorityColor(priority) {
    const colors = {
      low: 'text-green-600 bg-green-100',
      medium: 'text-yellow-600 bg-yellow-100',
      high: 'text-orange-600 bg-orange-100',
      urgent: 'text-red-600 bg-red-100'
    };
    return colors[priority] || colors.medium;
  }

  /**
   * Get type icon
   * @param {string} type - Follow-up type
   * @returns {string} Icon emoji
   */
  static getTypeIcon(type) {
    const icons = {
      call: '📞',
      email: '📧',
      meeting: '🤝',
      demo: '💻',
      proposal: '📋',
      message: '💬',
      other: '📝'
    };
    return icons[type] || '📝';
  }
}

export default FollowUpNotificationService;
